import { Reference, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import DirectUploadFileInput from '@components/fields/DirectUploadFileInput';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import Modal from '@components/Modal';
import { Opener } from '@hooks/useOpener';
import {
  SignedDocumentCreateDocument,
  SignedDocumentCreateMutation,
  SignedDocumentCreateMutationVariables,
  SignedDocumentFragmentDoc,
  SignedDocumentKindEnum,
  SignedDocumentMediumEnum
} from '@typing/Generated';

type Props = {
  journeyId: string;
  opener: Opener;
};

type FormData = {
  attachmentBlobId?: string;
  documentFor: string | null;
};

const ConsentFormUploader = ({ journeyId, opener }: Props) => {
  const intl = useIntl();
  const [fileInputBusy, setFileInputBusy] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [createSignedDocumentAction] = useMutation<SignedDocumentCreateMutation, SignedDocumentCreateMutationVariables>(
    SignedDocumentCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.signedDocumentCreate?.signedDocument) {
          const newSignedDocument = data.signedDocumentCreate.signedDocument;
          store.modify({
            fields: {
              signedDocuments(cachedSignedDocumentRefs: readonly Reference[]) {
                const newSignedDocumentRef = store.writeFragment({
                  data: newSignedDocument,
                  fragment: SignedDocumentFragmentDoc,
                  fragmentName: 'SignedDocument'
                });
                if (newSignedDocumentRef) {
                  return [...cachedSignedDocumentRefs, newSignedDocumentRef];
                }
                return [...cachedSignedDocumentRefs];
              }
            },
            id: store.identify({ __typename: 'Journey', id: journeyId })
          });
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      attachmentBlobId: yup.string().required(intl.formatMessage({ id: 'errors.consentForm.attachmentBlobId.blank' })),
      documentFor: yup.string().required(intl.formatMessage({ id: 'errors.consentForm.documentFor.blank' }))
    })
    .required();

  const initialValues = { attachmentBlobId: undefined, documentFor: null };

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const handleSubmit = useCallback(async () => {
    const documentFor = form.getValues().documentFor;
    if (documentFor) {
      setSubmitting(true);
      return createSignedDocumentAction({
        variables: {
          attachmentBlobId: form.getValues().attachmentBlobId,
          documentFor,
          journeyId,
          kind: SignedDocumentKindEnum.CONSENT_FORM,
          medium: SignedDocumentMediumEnum.UPLOAD
        }
      }).then(() => {
        setSubmitting(false);
        form.reset();
        opener.close();
      });
    }
  }, [createSignedDocumentAction, form, journeyId, opener]);

  const setAttachmentFileTypeError = useCallback(() => {
    form.setError('attachmentBlobId', {
      message: intl.formatMessage({ id: 'errors.consentForm.attachmentBlobId.invalid' }),
      type: 'validate'
    });
  }, [form, intl]);

  const clearAttachmentFileTypeError = useCallback(() => {
    form.clearErrors('attachmentBlobId');
  }, [form]);

  return (
    <Modal
      hideTitleBorder
      onCancel={opener.close}
      opener={opener}
      title={intl.formatMessage({ id: 'pages.consentFormUploader.title' })}
    >
      {() => (
        <Form hookForm={form} onSubmit={form.handleSubmit(handleSubmit)}>
          <Input<FormData>
            control={form.control}
            error={form.formState.errors.documentFor}
            label={intl.formatMessage({ id: 'pages.consentFormUploader.documentForLabel' })}
            name="documentFor"
            required
          />
          <DirectUploadFileInput<FormData, string>
            clearFileTypeError={clearAttachmentFileTypeError}
            control={form.control}
            errors={form.formState.errors.attachmentBlobId ? [form.formState.errors.attachmentBlobId] : []}
            label={intl.formatMessage({ id: 'pages.consentFormUploader.attachmentLabel' })}
            name="attachmentBlobId"
            onBusyChange={setFileInputBusy}
            required
            setFileTypeError={setAttachmentFileTypeError}
            setValue={value => {
              form.setValue('attachmentBlobId', value);
            }}
            validTypes={['application/pdf', 'image/jpg', 'image/jpeg', 'image/png', 'image/tiff']}
          />
          <FormButtons
            hookForm={form}
            onCancel={opener.close}
            saveDisabled={fileInputBusy || submitting}
            saveKey="dictionary.save"
          />
        </Form>
      )}
    </Modal>
  );
};

export default ConsentFormUploader;
